import {useMemo} from 'react';
import {RestAPI} from '../../services/rest-api';
import {createAuthHeader, RestClient} from '../../services/rest-client';
import useConfig from '../useConfig';
import {useQueryClient} from '@tanstack/react-query';
import {TokenResponse} from '../../api/auth-service/TokenResponse';
import {useAuthGlobalState} from '../useAuthState/useAuthGlobalState';
import {
  decodeAccessToken,
  decodeRefreshToken,
} from '@resurs-internal/core/lib/jwt';
import {ensure} from '../../helpers/ensure';

export const useRestAPI = () => {
  const auth = useAuthGlobalState();
  const client = useQueryClient();
  const config = useConfig();
  const api: RestAPI = useMemo(() => {
    return new RestAPI(
      new RestClient({
        enableLog: config.enableLog,
        apiKey: config?.apiKey,
        createAuthHeader: async () => {
          if (!auth?.state?.token) return '';
          const access = decodeAccessToken(auth?.state?.token);
          const accessExpireAtMillis = (access?.exp ?? 0) * 1000;
          // eslint-disable-next-line react-hooks/purity
          if (accessExpireAtMillis < Date.now() - auth.state.serverTimeOffset) {
            await api.client.refreshAuth();
          }
          return `Bearer ${auth?.state?.token}`;
        },
        refreshAuth: async (request) => {
          const _request = {
            clientId: auth.state?.clientId ?? config.clientId,
            loginType: auth?.state?.loginType,
            refreshToken: auth?.state.refreshToken,
            ...request,
          };

          try {
            if (_request.loginType === 'mbid') {
              const url = config.links.mbidRefresh;
              const response = await api.client.fetch({
                method: 'GET',
                url,
                headers: createAuthHeader(_request.refreshToken),
                isAuthRefresh: true,
              });
              const body = await api.client.decodeJSON<TokenResponse>(response);
              ensure(body, 'refresh mbid response body is undefined');

              const refreshExpireAtSeconds =
                decodeRefreshToken(body.refreshToken)?.exp ?? 0;
              const refreshExpireAt = refreshExpireAtSeconds * 1000;
              auth.update({
                clientId: request?.clientId,
                loginType: 'mbid',
                isLoggedIn: true,
                isLoggingIn: false,
                token: body.accessToken,
                refreshToken: body.refreshToken,
                refreshExpireAt: refreshExpireAt,
                links: {...auth.state.links, ...body._links},
              });
            }
            if (_request.loginType === 'oauth2') {
              const url = config.links.oAuthToken;
              const response = await api.oauth2.refresh(url, {
                grant_type: 'refresh_token',
                client_id: _request.clientId,
                refresh_token: _request.refreshToken,
              });
              ensure(response, 'refresh oauth2 response body is undefined');
              const refreshExpireIn = response.refresh_token_expires_in * 1000;
              // eslint-disable-next-line react-hooks/purity
              const refreshExpireAt = Date.now() + refreshExpireIn;
              auth.update({
                clientId: request?.clientId,
                loginType: 'oauth2',
                isLoggedIn: true,
                isLoggingIn: false,
                token: response.access_token,
                refreshToken: response.refresh_token,
                refreshExpireAt: refreshExpireAt,
                links: {...auth.state.links, ...response._links},
              });
            }
          } catch {
            client.clear();
            auth.reset();
          }
        },
      }),
    );
  }, [
    auth,
    config.enableLog,
    config.apiKey,
    config.clientId,
    config.links.mbidRefresh,
    config.links.oAuthToken,
    client,
  ]);
  return api;
};

export default useRestAPI;
